/**
 * Products — product analytics page (PHASE2_SPEC §4.5).
 *
 * Filter bar: type segment (All/Loan/Deposit/Other) · search box.
 * KPI tiles: # products · loan exposure · deposit exposure · top product
 * Bar chart: top 20 products by amount, colour-coded by type.
 * Sortable, paginated table of all products.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BarChart from '../../components/BarChart';
import Card from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import SortableTh from '../../components/SortableTh';
import StatusPill from '../../components/StatusPill';
import { formatDate } from '../../helpers/dateFormat';
import { npaFlag } from '../../helpers/flag';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { PRODUCT_TYPE_COLORS } from '../../helpers/palette';
import { useSortable } from '../../hooks/useSortable';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchProducts, selectProducts } from '../../redux/slices/dashboard';
import type { ProductRow, ProductType } from '../../type/api';

type Segment = 'ALL' | ProductType;

const PAGE_SIZE = 50;

export default function ProductsPage() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectProducts);

	const [segment, setSegment] = useState<Segment>('ALL');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(0);

	useEffect(() => {
		// fetch all then filter locally (the API already supports server-side
		// filtering — for v2 we'd lift segment+search into the request).
		if (cell.status === 'idle') {
			dispatch(fetchProducts());
		}
	}, [cell.status, dispatch]);

	const data = cell.data;
	const allProducts: ProductRow[] = useMemo(
		() => (Array.isArray(data?.products) ? data!.products : []),
		[data],
	);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return allProducts.filter((p) => {
			if (segment !== 'ALL' && p.productType !== segment) return false;
			if (!q) return true;
			return (
				p.productCode.toLowerCase().includes(q) ||
				p.productName.toLowerCase().includes(q)
			);
		});
	}, [allProducts, segment, search]);

	const sort = useSortable<ProductRow>(filtered, { field: 'totalCr', dir: 'desc' });

	const pagedRows = sort.rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
	const pageCount = Math.max(1, Math.ceil(sort.rows.length / PAGE_SIZE));

	useEffect(() => {
		// Reset to page 0 when filters change.
		setPage(0);
	}, [segment, search]);

	const handleRetry = () => dispatch(fetchProducts());

	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Product Analytics</h1></div></div>
				<Card><LoadingSkeleton rows={6} columns={6} /></Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Product Analytics</h1></div></div>
				<ErrorBanner message={cell.error || 'Failed to load products'} onRetry={handleRetry} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Product Analytics</h1></div></div>
				<Card><EmptyState title="No product data" /></Card>
			</div>
		);
	}

	const summary = data.summary;
	const top20 = sort.rows.slice(0, 20).map((p) => ({
		name: p.productCode,
		value: Math.abs(p.totalCr),
		color: PRODUCT_TYPE_COLORS[p.productType] || 'var(--navy-500)',
	}));
	const topProductLabel = sort.rows[0]
		? `${sort.rows[0].productCode} · ${sort.rows[0].productName}`
		: '—';

	return (
		<div className="page-body">
			<div className="page-head">
				<div>
					<h1 className="page-title">Product Analytics</h1>
					<div className="page-sub">
						{formatCount(summary.products)} products across the system.
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
				<button type="button" className="btn ghost" onClick={handleRetry}>Refresh</button>
			</div>

			<div className="grid cols-4">
				<KpiTile label="Products" value={formatCount(summary.products)} />
				<KpiTile label="Loan Exposure" value={formatCrore(summary.loanCr, 2)} />
				<KpiTile label="Deposit Exposure" value={formatCrore(summary.depositCr, 2)} />
				<KpiTile
					label="Top Product"
					value={<span style={{ fontSize: 16 }}>{topProductLabel}</span>}
					sub={sort.rows[0] ? formatAbsCrore(sort.rows[0].totalCr) : ''}
				/>
			</div>

			<Card title="Filters">
				<div className="filterbar">
					<div className="seg" role="tablist" aria-label="Product type">
						{(['ALL', 'LOAN', 'DEPOSIT', 'OTHER'] as Segment[]).map((s) => (
							<button
								key={s}
								type="button"
								className={segment === s ? 'active' : ''}
								onClick={() => setSegment(s)}
							>
								{s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
							</button>
						))}
					</div>
					<div className="search">
						<input
							type="search"
							value={search}
							placeholder="Search by code or name…"
							onChange={(e) => setSearch(e.target.value)}
							style={{ width: 300 }}
						/>
					</div>
					<div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-faint)' }}>
						{formatCount(filtered.length)} products
					</div>
				</div>
			</Card>

			<Card title="Top 20 products by amount (₹ Cr)">
				<BarChart data={top20} valueFormatter={(v) => formatCrore(v, 1)} />
				<div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12, color: 'var(--text-dim)' }}>
					{(Object.keys(PRODUCT_TYPE_COLORS) as ProductType[]).map((t) => (
						<span key={t}>
							<span aria-hidden="true" style={{
								display: 'inline-block', width: 10, height: 10, marginRight: 6,
								borderRadius: 2, background: PRODUCT_TYPE_COLORS[t],
							}} />
							{t}
						</span>
					))}
				</div>
			</Card>

			<Card title={`All products (${formatCount(sort.rows.length)})`}>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="productCode" sort={sort}>Code</SortableTh>
								<SortableTh field="productName" sort={sort}>Product</SortableTh>
								<SortableTh field="productType" sort={sort}>Type</SortableTh>
								<SortableTh field="totalCr" sort={sort} numeric>Total ₹Cr</SortableTh>
								<SortableTh field="accountCount" sort={sort} numeric>Accounts</SortableTh>
								<SortableTh field="bankCount" sort={sort} numeric>Banks</SortableTh>
								<SortableTh field="npaPct" sort={sort} numeric>NPA %</SortableTh>
							</tr>
						</thead>
						<tbody>
							{pagedRows.map((p) => (
								<tr
									key={`${p.productType}-${p.productCode}`}
									className="clickable"
									onClick={() => navigate(`/products/${encodeURIComponent(p.productCode)}?type=${p.productType}`)}
									tabIndex={0}
								>
									<td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{p.productCode}</td>
									<td>{p.productName}</td>
									<td><span className="chip muted">{p.productType}</span></td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(p.totalCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(p.accountCount)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(p.bankCount)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{p.productType === 'LOAN' ? (
											<StatusPill flag={npaFlag(p.npaPct)} variant="npa" compact label={formatPct(p.npaPct)} />
										) : (
											<span style={{ color: 'var(--text-faint)' }}>—</span>
										)}
									</td>
								</tr>
							))}
							{!pagedRows.length && (
								<tr><td colSpan={7}><EmptyState title="No products" hint="Try clearing filters." /></td></tr>
							)}
						</tbody>
					</table>
				</div>
				{pageCount > 1 && (
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
						<span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
							Page {page + 1} of {pageCount}
						</span>
						<div style={{ display: 'flex', gap: 6 }}>
							<button type="button" className="btn ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
								Prev
							</button>
							<button type="button" className="btn ghost" disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>
								Next
							</button>
						</div>
					</div>
				)}
			</Card>
		</div>
	);
}
